"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Star, Code, FileText, Target, Search, Map, Link2, Globe, ShieldCheck, TrendingUp, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const services = [{
  title: "Google Business Profile Optimization",
  desc: "I audit, claim, and optimize your Google Business Profile to capture high-intent map pack searches, driving direct calls and enquiries.",
  icon: MapPin,
  features: ["Map Pack Visibility", "NAP Consistency Audit", "Category & Attribute Setup"]
}, {
  title: "Local Citation Building & Audit",
  desc: "I audit existing local directory listings and build consistent, high-authority citations across UK and regional platforms.",
  icon: Building2,
  features: ["Duplicate Listing Cleanup", "UK Directory Submissions", "Data Aggregator Sync"]
}, {
  title: "Review Strategy & Management",
  desc: "I establish automated feedback workflows to systematically gain authentic Google reviews and enhance customer trust signals.",
  icon: Star,
  features: ["Review Generation Workflows", "Sentiment Tracking", "Google Review Response Guidance"]
}, {
  title: "Local Schema Markup",
  desc: "I write bespoke LocalBusiness JSON-LD schema so search engines accurately understand your business locations, services, and hours.",
  icon: Code,
  features: ["Custom JSON-LD Code", "Geo-Coordinates & Hours", "Service Area Specification"]
}, {
  title: "Localized Content Strategy",
  desc: "I design and write dedicated location and service landing pages tailored to local search query intent without doorway page spam.",
  icon: FileText,
  features: ["Location Page Audits", "Intent-Focused Copywriting", "On-Page SEO Structure"]
}, {
  title: "Geo-Targeted Keyword Strategy",
  desc: "I uncover hyper-local search phrases and intent opportunities that your competitors miss, prioritizing high-converting commercial keywords.",
  icon: Target,
  features: ["Hyper-Local Keyword Volume", "Competitor Gap Analysis", "Search Intent Classification"]
}, {
  title: "On-Page & Service Page SEO",
  desc: "I optimize page titles, meta descriptions, headings, and internal links to turn high organic search visibility into real qualified leads.",
  icon: Search,
  features: ["Conversion-Driven Meta Copy", "Internal Linking Silos", "Content Readability & UX"]
}, {
  title: "Google Maps Ranking Strategy",
  desc: "I implement targeted local optimization tactics to improve proximity signals, local engagement, and competitive grid positions.",
  icon: Map,
  features: ["Geogrid Rank Tracking", "Proximity Optimization", "Local Relevance Signals"]
}, {
  title: "Local Link & Regional PR Building",
  desc: "I secure genuine, geographically relevant backlinks from local news outlets, chambers of commerce, and regional industry sites.",
  icon: Link2,
  features: ["Regional Sponsorship Outreach", "Unlinked Mention Reclamation", "Local Press Releases"]
}, {
  title: "Multi-Location SEO Management",
  desc: "I scale SEO strategies across multiple physical branches or service territories, ensuring each location dominates its local search market.",
  icon: Globe,
  features: ["Multi-Branch Architecture", "Scalable Local Schema", "Branch Performance Dashboards"]
}, {
  title: "Technical Local SEO Audits",
  desc: "I perform rigorous technical audits to clear crawling barriers, fix mobile display issues, and optimize core web vitals for speed.",
  icon: ShieldCheck,
  features: ["Core Web Vitals Tuning", "Mobile-First UX Audits", "Crawl Budget & Indexation"]
}, {
  title: "Local Conversion Optimization",
  desc: "I refine your conversion paths so that incoming organic search traffic translates into measurable phone calls, forms, and bookings.",
  icon: TrendingUp,
  features: ["Click-to-Call Tracking", "Form Friction Reduction", "Call-to-Action Testing"]
}];
export default function Servicesgrid() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicesgrid" data-nav="light" className="bg-background text-foreground py-20 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <div className="max-w-7xl mx-auto space-y-16">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <Badge variant="secondary" className="bg-secondary text-secondary-foreground px-3.5 py-1 text-sm font-medium">
            <Sparkles className="size-3.5 mr-1 text-accent" />
            Direct Hands-On SEO Execution
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-serif">
            Specialist SEO Services Tailored for Sustainable Organic Growth
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-sans">
            When you work with YE Consultancy, you work directly with me—Yassine Essaadi.
            No account managers, junior delegates, or automated templates. Here are the core local and technical SEO services I deliver.
          </p>
        </div>

        {/* Services Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, i) => {
          const Icon = service.icon;
          return <motion.div key={service.title} variants={itemVariants} data-index={i}>
                <Card className="bg-card text-card-foreground border border-border shadow-md hover:shadow-xl rounded-xl transition-[box-shadow,border-color] duration-300 flex flex-col h-full overflow-hidden group">
                  {/* Card Header Banner */}
                  <div className="bg-primary text-primary-foreground p-6 flex items-start gap-4 rounded-t-xl transition-colors duration-300">
                    <div className="p-3 bg-primary-foreground/10 rounded-lg shrink-0">
                      <Icon className="size-6 text-primary-foreground" />
                    </div>
                    <div className="space-y-1">
                      <span className="text-xs uppercase font-medium tracking-wider text-primary-foreground/80">
                        Service {i + 1 < 10 ? `0${i + 1}` : i + 1}
                      </span>
                      <h3 className="text-lg font-semibold leading-snug font-serif">
                        {service.title}
                      </h3>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-6 flex-1 flex flex-col justify-between space-y-6">
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {service.desc}
                    </p>

                    {/* Features List */}
                    <div className="space-y-2.5 pt-2 border-t border-border/60">
                      {service.features.map((feature, fIdx) => <div key={fIdx} className="flex items-center gap-2 text-xs font-medium text-foreground">
                          <CheckCircle2 className="size-4 text-accent shrink-0" />
                          <span>{feature}</span>
                        </div>)}
                    </div>

                    {/* CTA */}
                    <div className="pt-2">
                      <Button asChild variant="outline" className="w-full justify-between group-hover:border-primary/50 group-hover:bg-accent/10 transition-[background-color,border-color] duration-200">
                        <Link to="/ContactPage">
                          <span>Request Details</span>
                          <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Consultation Banner */}
        <div className="bg-muted border border-border rounded-2xl p-8 sm:p-12 text-center space-y-6 max-w-4xl mx-auto shadow-sm">
          <div className="space-y-2">
            <h3 className="text-2xl sm:text-3xl font-semibold font-serif text-foreground">
              Need a Customized SEO Strategy for Your Business?
            </h3>
            <p className="text-muted-foreground text-base max-w-2xl mx-auto">
              Book a direct consultation with me to review your organic search rankings, local search presence, and technical performance.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <Button asChild size="lg" className="w-full sm:w-auto px-8">
              <Link to="/ContactPage">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto px-8">
              <Link to="/CaseStudiesPage">
                Explore Verified Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}